/**
 * User Administration API Service
 *
 * Handles all API calls for user administration and role management.
 * Updated for Phase 4 MSAL Migration - uses Bearer token authentication.
 * Updated for RBAC migration - uses role-based permissions.
 */

import { ApiService } from './api';
import { User, UserRole } from '../types/auth';

/**
 * List all users in the system
 * Requires global_admin privileges
 */
export async function listAllUsers(): Promise<User[]> {
  return ApiService.request<User[]>('/api/user-admin/users', {
    method: 'GET',
  });
}

/**
 * Update user role
 * Requires global_admin privileges
 *
 * @param userId - User ID to update
 * @param role - New role value
 * @returns Updated user object
 */
export async function updateUserRole(
  userId: number,
  role: UserRole
): Promise<User> {
  return ApiService.request<User>(
    `/api/user-admin/users/${userId}/role`,
    {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }
  );
}

/**
 * Update user superadmin status (legacy endpoint - uses role)
 * Requires global_admin privileges
 *
 * @param userId - User ID to update
 * @param isSuperadmin - Whether to grant global_admin role
 * @returns Updated user object
 */
export async function updateUserSuperadminStatus(
  userId: number,
  isSuperadmin: boolean
): Promise<User> {
  const role: UserRole = isSuperadmin ? 'global_admin' : 'managed_tenant_user';
  return ApiService.request<User>(
    `/api/user-admin/users/${userId}/superadmin`,
    {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }
  );
}

/**
 * Create new user in the system
 * Requires global_admin privileges
 *
 * @param userData - User creation data
 * @returns Created user object
 */
export async function createUser(userData: {
  email: string;
  name: string;
  azure_object_id: string;
  is_active: boolean;
  role: UserRole;
}): Promise<User> {
  return ApiService.request<User>('/api/user-admin/users', {
    method: 'POST',
    body: JSON.stringify(userData),
  });
}

/**
 * Delete user from the system
 * Requires global_admin privileges
 *
 * @param userId - User ID to delete
 */
export async function deleteUser(userId: number): Promise<void> {
  return ApiService.request<void>(`/api/user-admin/users/${userId}`, {
    method: 'DELETE',
  });
}

/**
 * Get user by User Principal Name (UPN)
 * Requires global_admin or organization_admin privileges
 *
 * @param upn - User Principal Name to search for (exact match)
 * @returns User if found, null otherwise
 */
export async function getUserByUpn(upn: string): Promise<User | null> {
  const users = await ApiService.request<User[]>(
    `/api/user-admin/users?upn=${encodeURIComponent(upn)}`,
    { method: 'GET' }
  );
  return users.length > 0 ? users[0] : null;
}

/**
 * Update user tenant admin status (legacy endpoint - uses role)
 * Requires global_admin privileges
 *
 * @param userId - User ID to update
 * @param isTenantAdmin - Whether to grant organization_admin role
 * @param tenantId - Optional tenant ID to associate with user
 * @returns Updated user object
 */
export async function updateUserTenantAdminStatus(
  userId: number,
  isTenantAdmin: boolean,
  tenantId?: string | null
): Promise<User> {
  const role: UserRole = isTenantAdmin ? 'organization_admin' : 'managed_tenant_user';
  return ApiService.request<User>(
    `/api/user-admin/users/${userId}/tenant-admin`,
    {
      method: 'PATCH',
      body: JSON.stringify({
        role,
        tenant_id: tenantId || null,
      }),
    }
  );
}

/**
 * Transfer user to a different organization
 * Requires global_admin privileges
 * Both source and target organizations must use home_tenant authentication mode
 *
 * @param userId - User ID to transfer
 * @param organizationId - Target organization ID
 * @returns Updated user object with new organization
 */
export async function transferUserOrganization(
  userId: number,
  organizationId: number
): Promise<User> {
  return ApiService.request<User>(
    `/api/user-admin/users/${userId}/organization`,
    {
      method: 'PATCH',
      body: JSON.stringify({ organization_id: organizationId }),
    }
  );
}
